import React, { useEffect, useMemo, useState, useRef } from 'react';

import { pick, omit, isEqual } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { Box, Typography, Grid, Divider } from '@material-ui/core';
import { ValidatorForm } from 'react-material-ui-form-validator';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';

import { CardComponent, SettingsForm, SortableListWrapper, FontSettingsControlWrapper } from 'components';
import { AutocompleteValidatorComponent, TextValidatorComponent } from 'components/FormComponents';
import { getFontSettingsWithNums, getFontSettingsWithPx } from 'utils';
import { errorResponseModel, planogramsModel } from 'models';
import { useSubmittingState } from 'hooks';
import {
  FontSettingsType,
  PlanogramObjectsResponse,
  PlanogramVersion,
  PlanogramVersionControlButton,
  PlanogramVersionUpdateParams,
} from 'types';
import { FontSettingsEditDialog } from 'components/Dialogs';
import {
  ALIGNMENTS,
  ALIGNMENTS_OPTIONS,
  NAVIGATION_BUTTON_TYPES,
  NAVIGATION_BUTTON_TYPES_OPTIONS,
} from 'constants/technical';
import { MenuButtonSettings } from 'pages/planograms/ManagePlanogramSettings/MenuButtonSettings/MenuButtonSettings';
import { PlanogramObjectData } from 'pages/planograms/ManagePlanogramSettings/MenuButtonSettings/DraggableMenuItems/InternalObjectSelect/helper';
import ControlButtonLinkBehaviour from 'pages/planograms/ManagePlanogramSettings/NavigationSettings/ControlButtonLinkBehaviour';
import { ControlButtonNavigationType } from 'types/enums';

import { ControlButtonSettings } from '../ControlButtonSettings/ControlButtonSettings';
import { SearchSettings } from '../SearchSettings/SearchSettings';

import {
  controlSettingsValues,
  internalSearchSettingsValues,
  menuSettingsValues,
  searchSettingsValues,
} from './constants';

import { Switch } from 'components/style';
import { InfoOutlinedIcon } from 'components/InfoTooltip/style';
import { NavigationAddButton, Chip } from './style';

const FORM_ID = 'navigationSettingsForm';

const defaultNavigationSettings: Partial<PlanogramVersionControlButton> = {
  title: 'Control Button',
  elementType: NAVIGATION_BUTTON_TYPES.CONTROL_BUTTON,
};

const defaultSettings: Partial<PlanogramVersion> = {
  navigationAlignment: ALIGNMENTS.MIDDLE,
  navigationDistributeEvenly: false,
  planogramVersionControlButtons: [],
};

const AllowedSphereObjects = {
  clusters: 'clusters',
  curves: 'curves',
  paths: 'paths',
  pictures: 'pictures',
  products: 'products',
  shapes: 'shapes',
  textAreas: 'textAreas',
  videos: 'videos',
};

interface NavigationSettingsProps {
  planogramVersion: PlanogramVersion;
  planogramId: number;
  handleServerError: (err: any) => void;
  setSuccessActionMessages: (messages: string[]) => void;
}

const NavigationSettings = ({
  planogramId,
  planogramVersion,
  handleServerError,
  setSuccessActionMessages,
}: NavigationSettingsProps) => {
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [toggle, setToggle] = useState(!!planogramVersion.planogramVersionControlButtons.length);
  const [fontSettingsEdit, setFontSettingsEdit] = useState<FontSettingsType | null>(null);
  const [settings, setSettings] = useState(defaultSettings);
  const [initialSettings, setInitialSettings] = useState(defaultSettings);
  const { isSubmitting, withHandlingSubmittingState } = useSubmittingState();
  const formRef = useRef<ValidatorForm>(null);
  const [planogramObjectData, setPlanogramObjectData] = useState<PlanogramObjectData[]>([]);

  const activeControlButton = useMemo(
    () => settings.planogramVersionControlButtons?.[activeTabIndex],
    [settings, activeTabIndex],
  );

  useEffect(() => {
    getNavigationSettings(planogramVersion);
    setPlanogramObjectData([]);
    errorResponseModel.getHostErrorResponses().then(({ planograms }) => {
      const formattedPlanograms = planograms.map(planogram => {
        const [lang, name] = planogram.urlPath.split('/');
        const formattedName = `${name}_${lang}`;
        const planogramObjects = {} as PlanogramObjectsResponse;

        Object.keys(AllowedSphereObjects).forEach(key => {
          planogramObjects[key as keyof PlanogramObjectsResponse] = [];
        });

        return {
          planogramName: formattedName,
          id: planogram.id,
          objects: planogramObjects,
        };
      });

      setPlanogramObjectData(formattedPlanograms);
    });
  }, []);

  const getNavigationSettings = (settings: PlanogramVersion) => {
    const parsedSettings = {
      navigationAlignment: settings.navigationAlignment,
      planogramVersionControlButtons: settings.planogramVersionControlButtons?.map(button => ({
        ...button,
        navigationValueSelect:
          button.navigationType === ControlButtonNavigationType.INTERNAL ? button.navigationValue : null,
        navigationValueUrl:
          button.navigationType === ControlButtonNavigationType.EXTERNAL ? button.navigationValue : null,
        planogramVersionControlButtonFont: getFontSettingsWithNums(
          button.planogramVersionControlButtonFont,
        ) as FontSettingsType,
      })),
      navigationDistributeEvenly: settings.navigationDistributeEvenly,
    } as Partial<PlanogramVersion>;

    setSettings(parsedSettings);
    setInitialSettings(parsedSettings);
  };

  const validateTabs = async () => {
    const formsValidation = settings.planogramVersionControlButtons?.map((button, index) => {
      setActiveTabIndex(index);

      return formRef.current?.isFormValid(true);
    }) as Promise<boolean>[];

    return Promise.all(formsValidation).then(formsState =>
      formsState.every((isValid, index) => {
        if (!isValid) {
          setActiveTabIndex(index);
          // @ts-ignore
          formRef.current?.submit();
        }

        return isValid;
      }),
    );
  };

  const onSubmit = withHandlingSubmittingState(async () => {
    const hasValidationError = await validateTabs();
    const dataToSave = !toggle
      ? {
          planogramVersionControlButtons: [],
        }
      : {
          ...settings,
          planogramVersionControlButtons: settings.planogramVersionControlButtons?.map(buttonSettings => {
            let navigationValue = null;

            if (buttonSettings.navigationType === ControlButtonNavigationType.INTERNAL)
              navigationValue = buttonSettings.navigationValueSelect;
            if (buttonSettings.navigationType === ControlButtonNavigationType.EXTERNAL)
              navigationValue = buttonSettings.navigationValueUrl;
            if (buttonSettings.elementType === NAVIGATION_BUTTON_TYPES.CONTROL_BUTTON) {
              return {
                ...buttonSettings,
                controlButtonId: buttonSettings.controlButton?.id ?? buttonSettings.controlButtonId,
                navigationValue,
                planogramVersionControlButtonFont: getFontSettingsWithPx(
                  buttonSettings.planogramVersionControlButtonFont,
                ),
              };
            }

            return {
              ...buttonSettings,
              controlButtonId: buttonSettings.controlButton?.id ?? buttonSettings.controlButtonId,
              planogramVersionControlButtonFont: getFontSettingsWithPx(
                buttonSettings.planogramVersionControlButtonFont,
              ),
            };
          }),
        };

    if (!hasValidationError) {
      return Promise.resolve();
    }

    return planogramsModel
      .editVersion(planogramId, planogramVersion.id, dataToSave as Partial<PlanogramVersionUpdateParams>)
      .then(data => {
        setSuccessActionMessages(data.success);
        getNavigationSettings(data.planogramVersion);
      })
      .catch(handleServerError);
  });

  const handleDelete = (index: number) => {
    setSettings(prevState => {
      const items = prevState.planogramVersionControlButtons?.filter((_, i) => i !== index) ?? [];

      if (!items.length) setToggle(false);
      if (activeTabIndex === index) setActiveTabIndex(0);

      return {
        ...prevState,
        planogramVersionControlButtons: items,
      };
    });
  };

  const handleButtonChanges = (item: Partial<PlanogramVersionControlButton>) => {
    setSettings(
      prevState =>
        ({
          ...prevState,
          planogramVersionControlButtons: prevState.planogramVersionControlButtons?.map(
            (button: PlanogramVersionControlButton, index: number) =>
              index === activeTabIndex
                ? {
                    ...getAllowedProperties(button),
                    ...item,
                  }
                : { ...button },
          ),
        } as Partial<PlanogramVersion>),
    );
  };

  const handleChangeElementType = (item: Partial<PlanogramVersionControlButton>) => {
    const isDefaultTitle = NAVIGATION_BUTTON_TYPES_OPTIONS.some(option => option.label === activeControlButton?.title);
    const buttonTitle = isDefaultTitle
      ? NAVIGATION_BUTTON_TYPES_OPTIONS.find(option => option.id === item.elementType)?.label
      : activeControlButton?.title;

    if (item.elementType === NAVIGATION_BUTTON_TYPES.MENU) {
      handleButtonChanges({
        elementType: item.elementType,
        title: buttonTitle,
        menu: {
          ...planogramVersion.defaultNavigation.menu,
          menuItems: [planogramVersion.defaultNavigation.menuItem],
        },
        controlButton: planogramVersion.defaultNavigation.planogramVersionControlButtons.find(
          button => button.elementType === NAVIGATION_BUTTON_TYPES.MENU,
        )?.controlButton,
      });
    }
    if (item.elementType === NAVIGATION_BUTTON_TYPES.SEARCH) {
      handleButtonChanges({
        elementType: item.elementType,
        title: buttonTitle,
        searchSetting: planogramVersion.defaultNavigation.searchSetting,
        controlButton: planogramVersion.defaultNavigation.planogramVersionControlButtons.find(
          button => button.elementType === NAVIGATION_BUTTON_TYPES.SEARCH,
        )?.controlButton,
      });
    }
    if (item.elementType === NAVIGATION_BUTTON_TYPES.CONTROL_BUTTON) {
      handleButtonChanges({
        elementType: item.elementType,
        title: buttonTitle,
        controlButton: planogramVersion.defaultNavigation.planogramVersionControlButtons.find(
          button => button.elementType === NAVIGATION_BUTTON_TYPES.CONTROL_BUTTON,
        )?.controlButton,
      });
    }
  };

  const handleFontSettingsSave = (settings: FontSettingsType) => {
    setFontSettingsEdit(null);
    handleButtonChanges({
      planogramVersionControlButtonFont: { ...settings },
    });
  };

  const closeFontSettingsDialog = () => setFontSettingsEdit(null);

  const handleFontSettingsEdit = (settings: FontSettingsType) => setFontSettingsEdit({ ...settings });

  const handleOrderChanges = (list: PlanogramVersionControlButton[]) => {
    const newActiveTabIndex = list.findIndex(item => item === activeControlButton);

    setActiveTabIndex(newActiveTabIndex);
    setSettings(prevState => ({
      ...prevState,
      planogramVersionControlButtons: [...list],
    }));
  };

  const handleToggle = () => {
    if (!settings.planogramVersionControlButtons?.length) {
      handleAdd();
    }

    setToggle(prevState => !prevState);
  };

  const handleChange = (item: Record<string, boolean>) =>
    setSettings(prevState => ({
      ...prevState,
      ...item,
    }));

  const getAllowedProperties = (item: PlanogramVersionControlButton) => {
    switch (item.elementType) {
      case NAVIGATION_BUTTON_TYPES.CONTROL_BUTTON:
        return pick(item, controlSettingsValues);
      case NAVIGATION_BUTTON_TYPES.SEARCH:
        return pick(item, searchSettingsValues);
      case NAVIGATION_BUTTON_TYPES.MENU:
        return pick(item, menuSettingsValues);
      default:
        return {};
    }
  };

  const handleAdd = () =>
    setSettings(prevState => {
      const controlButton = planogramVersion.defaultNavigation.planogramVersionControlButtons.find(
        button => button.elementType === NAVIGATION_BUTTON_TYPES.CONTROL_BUTTON,
      );

      if (!controlButton) return prevState;
      const newItem = {
        ...controlButton,
        ...defaultNavigationSettings,
        planogramVersionControlButtonFont: getFontSettingsWithNums(
          controlButton.planogramVersionControlButtonFont,
        ) as FontSettingsType,
      };

      if (!prevState.planogramVersionControlButtons?.length) setActiveTabIndex(0);

      return {
        ...prevState,
        planogramVersionControlButtons: [
          ...(prevState.planogramVersionControlButtons as PlanogramVersionControlButton[]),
          newItem,
        ],
      } as Partial<PlanogramVersion>;
    });

  const isSettingsChanged = () => {
    const data = {
      ...settings,
      planogramVersionControlButtons: settings.planogramVersionControlButtons?.map(button => {
        if (button.elementType === NAVIGATION_BUTTON_TYPES.SEARCH) {
          return {
            ...omit(getAllowedProperties(button), [
              'searchActiveIcon',
              'searchSettingFont',
              'noResultSearchSettingFont',
            ]),
            searchSetting: {
              ...pick(button.searchSetting, internalSearchSettingsValues),
              searchSettingFont: { ...omit(button.searchSetting.searchSettingFont, 'fieldName') },
              noResultSearchSettingFont: { ...omit(button.searchSetting.noResultSearchSettingFont, 'fieldName') },
            },
          };
        }
        return {
          ...omit(getAllowedProperties(button), ['searchActiveIcon', 'searchSettingFont', 'noResultSearchSettingFont']),
        };
      }),
    };
    const initialData = {
      ...initialSettings,
      planogramVersionControlButtons: initialSettings.planogramVersionControlButtons?.map((button, index) => {
        if (button.elementType === NAVIGATION_BUTTON_TYPES.SEARCH) {
          return {
            ...getAllowedProperties(button),
            searchSetting: pick(button.searchSetting, internalSearchSettingsValues),
          };
        }
        return getAllowedProperties(button);
      }),
    };

    return toggle !== !!settings.planogramVersionControlButtons?.length || !isEqual(data, initialData);
  };

  return (
    <SettingsForm
      isSaveButtonVisible={!isSubmitting && isSettingsChanged()}
      handleSave={onSubmit}
      id={FORM_ID}
      refForm={formRef}
    >
      <CardComponent
        title="Navigation buttons"
        isLoading={isSubmitting}
        CardHeaderNav={() => <Switch checked={toggle} onChange={handleToggle} />}
      >
        {toggle && settings.planogramVersionControlButtons && (
          <>
            <Grid container alignItems="center">
              <Grid item style={{ flexGrow: 1, minWidth: 0 }}>
                <Box display="flex" flexGrow={1} mr={5} minWidth={0}>
                  <SortableListWrapper
                    list={settings.planogramVersionControlButtons}
                    setList={handleOrderChanges}
                    forceFallback
                    fallbackTolerance={3}
                    style={{ display: 'flex', maxWidth: 'calc(100% - 34px)' }}
                  >
                    {settings.planogramVersionControlButtons.map(
                      (button: PlanogramVersionControlButton, index: number) => (
                        <Chip
                          key={`navigation-settings-${index}`}
                          color={index === activeTabIndex ? 'primary' : 'default'}
                          label={button.title}
                          deleteIcon={<CloseIcon />}
                          onClick={() => setActiveTabIndex(index)}
                          onDelete={() => handleDelete(index)}
                        />
                      ),
                    )}
                  </SortableListWrapper>
                  <NavigationAddButton
                    disabled={settings.planogramVersionControlButtons.length >= 5}
                    Icon={AddIcon}
                    onClick={handleAdd}
                  />
                </Box>
              </Grid>
              <Grid item>
                <Box display="flex" my={2}>
                  <InfoOutlinedIcon className="regular" />
                  <Box ml={1}>
                    <Typography color="textSecondary" variant="caption">
                      You can add up to 5 navigation elements and drag & drop to change their order.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
            <Grid item sm={12}>
              <Box mt={6} mb={3}>
                <Typography variant="h4">Navigation layout</Typography>
              </Box>
              <Grid item sm={4} style={{ maxWidth: '12.5vw' }}>
                <AutocompleteValidatorComponent
                  value={settings.navigationAlignment ?? ''}
                  fieldName="navigationAlignment"
                  handleChange={(state: Record<string, string>) =>
                    setSettings(prevState => ({ ...prevState, ...state }))
                  }
                  itemsNamePath="label"
                  disableMinHeight
                  disableClearable
                  selectList={ALIGNMENTS_OPTIONS}
                  label="Alignment"
                />
              </Grid>
              <Box ml={-2.5} display="flex" alignItems="center">
                <Switch
                  checked={settings.navigationDistributeEvenly}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange({ navigationDistributeEvenly: event.target.checked })
                  }
                />
                <Typography variant="caption">Distribute evenly</Typography>
              </Box>
              <Box my={6}>
                <Divider />
              </Box>
              <Grid item sm={12}>
                <Box mb={3}>
                  <Typography variant="h4">Navigation element</Typography>
                </Box>
                <Grid container>
                  <FontSettingsControlWrapper
                    fontSettings={activeControlButton?.planogramVersionControlButtonFont}
                    handleSettingsEdit={handleFontSettingsEdit}
                  >
                    <Grid item sm={5}>
                      <TextValidatorComponent
                        label="Title"
                        value={activeControlButton?.title ?? ''}
                        fieldName="title"
                        handleChange={handleButtonChanges}
                      />
                    </Grid>
                  </FontSettingsControlWrapper>
                  <FontSettingsEditDialog
                    settings={fontSettingsEdit}
                    handleClose={closeFontSettingsDialog}
                    handleSet={handleFontSettingsSave}
                  />
                  <Grid item style={{ marginLeft: 'auto' }}>
                    <Box mt={2}>
                      <Typography variant="caption">Show title on screen</Typography>
                      <Switch
                        checked={activeControlButton?.showTitle ?? false}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          handleButtonChanges({ showTitle: event.target.checked })
                        }
                      />
                    </Box>
                  </Grid>
                </Grid>
                <Grid item sm={5}>
                  <AutocompleteValidatorComponent
                    value={activeControlButton?.elementType ?? ''}
                    fieldName="elementType"
                    handleChange={handleChangeElementType}
                    itemsNamePath="label"
                    disableMinHeight
                    disableClearable
                    selectList={NAVIGATION_BUTTON_TYPES_OPTIONS}
                    getOptionDisabled={(option: Record<string, string>) =>
                      option.label === 'Product Search' &&
                      settings.planogramVersionControlButtons?.length &&
                      settings.planogramVersionControlButtons.findIndex(
                        item => item.elementType === NAVIGATION_BUTTON_TYPES.SEARCH,
                      ) >= 0
                    }
                    label="Type"
                  />
                </Grid>
                {activeControlButton?.elementType === NAVIGATION_BUTTON_TYPES.SEARCH && (
                  <Box mt={1} ml={-1} display="flex">
                    <InfoOutlinedIcon className="regular" />
                    <Box ml={1}>
                      <Typography color="textSecondary" variant="caption">
                        Product Search should only be added if you are using Products in your sphere as search is linked
                        to product data.
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Grid>
              {activeControlButton?.elementType === NAVIGATION_BUTTON_TYPES.CONTROL_BUTTON && (
                <Grid item sm={12}>
                  <Box mt={6}>
                    <ControlButtonSettings setSettings={handleButtonChanges} settings={activeControlButton} />
                    <ControlButtonLinkBehaviour
                      handleChange={handleButtonChanges}
                      navigationType={activeControlButton.navigationType ?? ControlButtonNavigationType.EXTERNAL}
                      navigationValueSelect={activeControlButton.navigationValueSelect ?? undefined}
                      navigationValueUrl={activeControlButton.navigationValueUrl ?? undefined}
                      checkedOpenInNewPage={activeControlButton.openInNewPage ?? false}
                      withoutNavigation={activeControlButton.withoutNavigation ?? false}
                      planogramObjectData={planogramObjectData}
                    />
                  </Box>
                </Grid>
              )}
              {activeControlButton?.elementType === NAVIGATION_BUTTON_TYPES.MENU && (
                <Grid item sm={12}>
                  <Box mt={6}>
                    <ControlButtonSettings setSettings={handleButtonChanges} settings={activeControlButton} />
                    <MenuButtonSettings
                      planogramVersion={planogramVersion}
                      settings={activeControlButton}
                      setSettings={handleButtonChanges}
                      planogramObjectData={planogramObjectData}
                    />
                  </Box>
                </Grid>
              )}
              {activeControlButton?.elementType === NAVIGATION_BUTTON_TYPES.SEARCH && (
                <Grid item sm={12}>
                  <Box mt={9}>
                    <ControlButtonSettings
                      setSettings={handleButtonChanges}
                      settings={activeControlButton}
                      title="Default state"
                    />
                    <SearchSettings
                      planogramVersion={planogramVersion}
                      handleServerError={handleServerError}
                      setSettings={handleButtonChanges}
                      tabSettings={activeControlButton}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </>
        )}
      </CardComponent>
    </SettingsForm>
  );
};

export default NavigationSettings;
